// ═══════════════════════════════════════
// ATLAS BEASTS — AUTH STORE
// Custom JWT — No third party auth
// ═══════════════════════════════════════

import { useState, useCallback, createContext, useContext } from 'react'
import type { AuthState, Player, ApiResponse, FactionId } from '../types'

const API_BASE = import.meta.env.DEV
  ? '/api'
  : (import.meta.env.VITE_API_URL || 'https://atlas-beasts-api.ploonu.workers.dev')

// ── Storage keys ──
const TOKEN_KEY = 'ab_token'
const PLAYER_KEY = 'ab_player'

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setStoredToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

export function getStoredPlayer(): Player | null {
  try {
    const raw = localStorage.getItem(PLAYER_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export function setStoredPlayer(player: Player): void {
  localStorage.setItem(PLAYER_KEY, JSON.stringify(player))
}

export function clearStoredPlayer(): void {
  localStorage.removeItem(PLAYER_KEY)
}

export function parseJWT(token: string): { playerId: string; username: string; exp: number } | null {
  try {
    const payload = token.split('.')[1]
    const decoded = JSON.parse(atob(payload))
    return decoded
  } catch {
    return null
  }
}

export function isTokenExpired(token: string): boolean {
  const decoded = parseJWT(token)
  if (!decoded) return true
  return Date.now() / 1000 > decoded.exp
}

// ── Local PIN storage ──
const PIN_KEY = 'ab_pin'

export function setStoredPin(pin: string): void {
  localStorage.setItem(PIN_KEY, btoa(pin))
}

export function verifyStoredPin(pin: string): boolean {
  const stored = localStorage.getItem(PIN_KEY)
  if (!stored) return true // no PIN set — skip verification
  return stored === btoa(pin)
}

export function hasStoredPin(): boolean {
  return !!localStorage.getItem(PIN_KEY)
}

export function clearStoredPin(): void {
  localStorage.removeItem(PIN_KEY)
}

// ── Local account creation (no backend required) ──
export function createLocalToken(playerId: string, username: string): string {
  const header = btoa(JSON.stringify({ alg: 'local', typ: 'JWT' }))
  const exp = Math.floor(Date.now() / 1000) + 10 * 365 * 24 * 3600 // 10 years
  const payload = btoa(JSON.stringify({ playerId, username, exp }))
  return `${header}.${payload}.local`
}

export function createLocalPlayer(
  username: string,
  email: string,
  faction: FactionId
): Player {
  return {
    id: crypto.randomUUID(),
    username,
    email,
    level: 1,
    xp: 0,
    faction,
    zoneId: 'ashwake',
    atlasCoins: 500,
    shards: 0,
    streakDays: 0,
    lastActive: new Date().toISOString(),
    isFoundingMember: false,
    createdAt: new Date().toISOString(),
  }
}

// ── Auth API calls (used when backend is live) ──
export async function refreshToken(token: string): Promise<ApiResponse<{ token: string }>> {
  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  })
  return res.json()
}

export async function getMe(token: string): Promise<ApiResponse<Player>> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 5000)
  try {
    const res = await fetch(`${API_BASE}/player/me`, {
      headers: { 'Authorization': `Bearer ${token}` },
      signal: controller.signal,
    })
    return res.json()
  } finally {
    clearTimeout(timeout)
  }
}

export async function logout(token: string): Promise<void> {
  try {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    })
  } finally {
    clearStoredToken()
    clearStoredPlayer()
  }
}

// ── Auth Hook ──
export function useAuthState() {
  const [state, setState] = useState<AuthState>({
    player: null,
    token: null,
    isLoading: true,
    isAuthenticated: false,
  })

  const initialize = useCallback(async () => {
    const token = getStoredToken()
    const localPlayer = getStoredPlayer()

    if (!token || isTokenExpired(token)) {
      clearStoredToken()
      clearStoredPlayer()
      setState(s => ({ ...s, isLoading: false }))
      return
    }

    // Hydrate immediately from localStorage so UI is instant
    if (localPlayer) {
      setState({ player: localPlayer, token, isLoading: false, isAuthenticated: true })
    }

    // Background-sync fresh player data from server (non-blocking)
    try {
      const res = await getMe(token)
      if (res.success && res.data) {
        setStoredPlayer(res.data)
        setState({ player: res.data, token, isLoading: false, isAuthenticated: true })
      } else if (!localPlayer) {
        // Only clear auth if we had no local data to fall back on
        clearStoredToken()
        clearStoredPlayer()
        setState(s => ({ ...s, isLoading: false }))
      }
    } catch {
      // Network offline — keep local player, stay authenticated
      if (!localPlayer) {
        clearStoredToken()
        clearStoredPlayer()
        setState(s => ({ ...s, isLoading: false }))
      }
    }
  }, [])

  const setAuth = useCallback((player: Player, token: string) => {
    setStoredToken(token)
    setStoredPlayer(player)
    setState({ player, token, isLoading: false, isAuthenticated: true })
  }, [])

  const refreshPlayer = useCallback(async () => {
    const token = getStoredToken()
    if (!token) return
    try {
      const res = await getMe(token)
      if (res.success && res.data) {
        setStoredPlayer(res.data)
        setState(s => ({ ...s, player: res.data! }))
      }
    } catch { /* offline — keep existing */ }
  }, [])

  const signOut = useCallback(async () => {
    const token = getStoredToken()
    if (token) {
      try { await logout(token) } catch { /* ignore if API not available */ }
    }
    clearStoredToken()
    clearStoredPlayer()
    clearStoredPin()
    setState({ player: null, token: null, isLoading: false, isAuthenticated: false })
  }, [])

  return { state, initialize, setAuth, signOut, refreshPlayer }
}

// ── Auth Context (wire up in App.tsx) ──
export interface AuthContextType {
  player: Player | null
  token: string | null
  isLoading: boolean
  isAuthenticated: boolean
  setAuth: (player: Player, token: string) => void
  signOut: () => Promise<void>
  refreshPlayer: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType | null>(null)

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
